import React from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { BRAND, FI_ORANGE } from '../theme/tokens';
import { BTN_STANDARD, BTN_STATUS_GREEN, PO_COLORS } from '../theme/poButtonTokens';
import {
  ImportResolutionBadge, ImportLineActions, PreviewBatchButton, InfoableButton,
  type ButtonInfo,
} from './ImportResolutionBadge';
import type { ImportLineState, ImportUserAction } from '../utils/codeMatching';
import type { ParsedImportLine } from '../utils/importPreviewWorkflow';

const PREVIEW_BTN_INFO: Record<'useSupplier' | 'autoCreate' | 'allService' | 'cancel', ButtonInfo> = {
  useSupplier: {
    title: 'Add lines · use supplier codes',
    description: 'Each product line uses the supplier’s stock code as your inventory code. Creates catalogue items where missing. Best when you want supplier codes as your internal codes.',
    example: '9 Bunnings lines → 9 PO lines with inventory codes 1234567, 7654321, … matching the invoice.',
  },
  autoCreate: {
    title: 'Add lines · auto-create internal codes',
    description: 'Mints a unique inventory code per new line using your company prefix rule (e.g. FIE-234, FIE-235…). Supplier codes stay linked in the price book when that column is on. Already-matched lines keep their existing inventory code.',
    example: '9 new lines → FIE-234…FIE-242, each unique, never colliding with codes already in the catalogue.',
  },
  allService: {
    title: 'All new → service',
    description: 'Marks every remaining New line as a Service line (labour, freight, call-out fees). Service lines are description-only — no inventory code is required.',
    example: 'An invoice line "Call-out fee — Saturday repair" has no supplier code. Marking it Service keeps it on the bill without code chasing.',
  },
  cancel: {
    title: 'Cancel',
    description: 'Discards this parse entirely. Nothing is added, no inventory codes are created, and your price book is not touched.',
    example: 'You uploaded the wrong supplier\'s invoice. Cancel throws away the extracted lines.',
  },
};

export interface SupplierImportPreviewDialogProps {
  open: boolean;
  lines: ParsedImportLine[];
  resolutions: Record<number, ImportLineState>;
  targetLabel: string;
  targetKind: 'po' | 'bill' | 'quote' | 'invoice';
  existingLineCount: number;
  trackSupplierCodes: boolean;
  previewNewCount: number;
  demo?: boolean;
  demoMessage?: string;
  documentType?: string;
  supplierInvoiceNo?: string;
  quoteNumber?: string;
  fileName?: string;
  /** Kept for API parity — PDF preview lives on the host page, not this modal. */
  fileUrl?: string | null;
  fileType?: string;
  previewCollapsed: boolean;
  onTogglePreviewCollapsed: () => void;
  onSetPreviewAction: (idx: number, action: ImportUserAction) => void;
  onBatchAction: (action: 'autoCreate' | 'service') => void;
  /** Add all lines using each supplier code as the inventory code. */
  onUseSupplierCodes: () => void;
  /** Add all lines, minting unique PREFIX-N internal codes for new items. */
  onAutoCreateInternalCodes: () => void;
  onDiscard: () => void;
}

/**
 * Pre-parse import review — two explicit add paths:
 *   1. Use supplier codes as inventory codes
 *   2. Auto-create unique internal codes (PREFIX-N rules)
 * Supplier Code column is intentionally omitted here — codes are chosen via the
 * footer actions; the PO/Bill line table shows Supplier Code when that setting is on.
 */
export const SupplierImportPreviewDialog: React.FC<SupplierImportPreviewDialogProps> = ({
  open, lines, resolutions, targetLabel, targetKind,
  demo, demoMessage, documentType, supplierInvoiceNo, quoteNumber,
  fileName, onSetPreviewAction, onBatchAction, onUseSupplierCodes, onAutoCreateInternalCodes, onDiscard,
}) => {
  const targetNoun = targetKind === 'po' ? 'PO'
    : targetKind === 'bill' ? 'bill'
    : targetKind === 'quote' ? 'quote'
    : 'invoice';
  const n = lines.length;
  const lineWord = n === 1 ? 'line' : 'lines';

  return (
    <Dialog open={open} onClose={onDiscard} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: `2px solid ${BRAND}33` } }}>
      <DialogTitle sx={{ pb: 1, pt: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: BRAND }}>
            Import preview — {n} {lineWord}
          </Typography>
          {fileName && (
            <Chip size="small" label={fileName}
              sx={{ height: 20, fontSize: '0.62rem', fontWeight: 600, maxWidth: 240,
                backgroundColor: '#f3eefc', color: BRAND }} />
          )}
        </Stack>
        <Typography sx={{ fontSize: '0.7rem', color: '#888', mt: 0.2 }}>
          {documentType === 'invoice' ? 'Tax invoice' : documentType === 'quote' ? 'Quote' : 'Supplier document'}
          {supplierInvoiceNo ? ` · ${supplierInvoiceNo}` : ''}
          {quoteNumber ? ` · ${quoteNumber}` : ''}
          {' · '}Adding to <strong>{targetLabel}</strong>
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#555', mt: 0.8, fontWeight: 600 }}>
          How should inventory codes be set?
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5 }}>
        {demo && demoMessage && (
          <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.72rem', py: 0.3 }}>
            {demoMessage}
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Status', 'Type', 'Inventory Code', 'Description', 'Qty', 'Price', ''].map(h => (
                <TableCell key={h || 'actions'} sx={{ fontWeight: 700, fontSize: '0.68rem', py: 0.5, color: BRAND, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, i) => {
              const res = resolutions[i];
              const isService = res?.status === 'service' || res?.userAction === 'service';
              const showInternal = isService
                ? '—'
                : (res?.internalCode || res?.suggestedInternal || (res?.userAction === 'autoCreate' ? '(auto-create)' : 'needs code'));
              return (
                <TableRow key={i}>
                  <TableCell><ImportResolutionBadge state={res} /></TableCell>
                  <TableCell>
                    <Tooltip title={isService ? 'Service — click to make Product' : 'Product — click to make Service'}>
                      <Chip
                        label={isService ? 'Service' : 'Product'} size="small"
                        onClick={() => onSetPreviewAction(i, isService ? 'pending' : 'service')}
                        sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, cursor: 'pointer',
                          backgroundColor: isService ? '#eceff1' : PO_COLORS.chipTint,
                          color: isService ? '#546e7a' : '#9A3412' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700,
                    color: isService ? '#546e7a' : showInternal !== 'needs code' ? FI_ORANGE : '#c62828' }}>
                    {showInternal}
                    {res?.matchMethod && res?.suggestedInternal && !isService && (
                      <Typography component="span" sx={{ fontSize: '0.58rem', color: '#aaa', ml: 0.5 }}>
                        ({res.matchMethod})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>{l.description}</TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>{l.qty ?? 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>${(l.unitPrice ?? 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <ImportLineActions state={res} onAction={a => onSetPreviewAction(i, a)} compact />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.8, flexDirection: 'column', alignItems: 'stretch', gap: 1.2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          <InfoableButton info={PREVIEW_BTN_INFO.useSupplier}>
            <Button variant="contained" disableElevation onClick={onUseSupplierCodes}
              sx={{ ...BTN_STANDARD, flex: 1, minWidth: 200, py: 1 }}>
              Add {n} {lineWord} · use supplier codes
            </Button>
          </InfoableButton>
          <InfoableButton info={PREVIEW_BTN_INFO.autoCreate}>
            <Button variant="contained" disableElevation onClick={onAutoCreateInternalCodes}
              sx={{ ...BTN_STATUS_GREEN, flex: 1, minWidth: 200, py: 1 }}>
              Add {n} {lineWord} · auto-create internal codes
            </Button>
          </InfoableButton>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <InfoableButton info={PREVIEW_BTN_INFO.allService}>
            <PreviewBatchButton label="All new → service" muted onClick={() => onBatchAction('service')} />
          </InfoableButton>
          <InfoableButton info={PREVIEW_BTN_INFO.cancel}>
            <Button onClick={onDiscard}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: '#666',
                '&:hover': { backgroundColor: '#f5f5f5' } }}>
              Cancel
            </Button>
          </InfoableButton>
        </Box>
        <Typography sx={{ fontSize: '0.62rem', color: '#999', textAlign: 'center' }}>
          Auto-create uses your prefix rule (e.g. FIE-234) and guarantees each new code is unique in the catalogue.
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierImportPreviewDialog;
